import type { Theme } from '@personalizehq/schemas/ir';

// COLOUR MATHS FOR MINTING. Pure, dependency-free, and in `editor` rather than `apps/web` because it
// is DOCUMENT logic: the same derivation must serve the inspector, the theme panel, and eventually
// the MCP authoring writer, and all three have to agree on what dark half a colour gets.
//
// Everything works in OKLab/OKLCH (Björn Ottosson's). The reason is the whole point of the feature:
// we need to move a colour's LIGHTNESS while holding what a human calls "the same colour". In HSL,
// `hsl(60 100% 50%)` (yellow) and `hsl(240 100% 50%)` (blue) claim the same lightness and are wildly
// different perceptual brightnesses, so an HSL flip washes out yellows and crushes blues. OKLab is
// perceptually uniform, so one formula works for every hue.

interface Lab {
  l: number;
  a: number;
  b: number;
}
export interface Lch {
  /** Perceptual lightness, 0…1. */
  l: number;
  /** Chroma, 0…~0.4 in sRGB. */
  c: number;
  /** Hue angle in degrees, 0…360. */
  h: number;
}

// ---------------------------------------------------------------------------- sRGB <-> OKLab

/** The sRGB transfer function. NOT a plain 2.2 gamma — the linear toe near black is what keeps dark
 *  colours from collapsing to the same value, and those are exactly the colours a dark-mode
 *  derivation produces. */
const toLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c: number): number => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function linearToOklab(r: number, g: number, b: number): Lab {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

function oklabToLinear(lab: Lab): { r: number; g: number; b: number } {
  const l = (lab.l + 0.3963377774 * lab.a + 0.2158037573 * lab.b) ** 3;
  const m = (lab.l - 0.1055613458 * lab.a - 0.0638541728 * lab.b) ** 3;
  const s = (lab.l - 0.0894841775 * lab.a - 1.291485548 * lab.b) ** 3;
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

// ---------------------------------------------------------------------------- hex <-> OKLCH

/** Accepts `#rgb`, `#rrggbb`, and `#rrggbbaa` (alpha is DROPPED — it is not part of the derivation,
 *  and a colour token's two halves must agree on it). Anything unparseable returns mid-grey rather
 *  than throwing: this runs on author input, and a malformed hex must not take the panel down. */
export function toOklch(hex: string): Lch {
  const parsed = parseHex(hex);
  if (!parsed) return { l: 0.5, c: 0, h: 0 };
  const lab = linearToOklab(toLinear(parsed.r), toLinear(parsed.g), toLinear(parsed.b));
  const c = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  // A neutral has no meaningful hue; pinning it to 0 keeps `toHex(toOklch(x))` stable instead of
  // letting floating-point noise in `a`/`b` pick a random angle.
  const h = c < 1e-6 ? 0 : ((Math.atan2(lab.b, lab.a) * 180) / Math.PI + 360) % 360;
  return { l: lab.l, c, h };
}

/** OKLCH -> `#rrggbb`, GAMUT-CLAMPED. A colour pushed lighter or darker very often leaves sRGB, and
 *  naively clipping each channel shifts the hue — a light indigo clips its blue channel first and
 *  arrives looking pink. Reducing CHROMA instead holds the hue and desaturates, which is what every
 *  colour system does and what a human reads as "the same colour, less intense". */
export function toHex(lch: Lch): string {
  const l = clamp(lch.l, 0, 1);
  let lo = 0;
  let hi = Math.max(0, lch.c);
  // 24 halvings resolves chroma far finer than an 8-bit channel can express, so this is exact for
  // our purposes and still bounded.
  if (!inGamut(l, hi, lch.h)) {
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(l, mid, lch.h)) lo = mid;
      else hi = mid;
    }
  } else {
    lo = hi;
  }
  const rgb = lchToRgb(l, lo, lch.h);
  return `#${[rgb.r, rgb.g, rgb.b].map(channel).join('')}`;
}

// ---------------------------------------------------------------------------- the derivation

/** The default surface anchors, used when the theme names none. Not 0 and 1: a real "dark mode"
 *  surface is a very dark grey, not black, and a light surface is white-ish. */
const DEFAULT_LIGHT_SURFACE = 0.98;
const DEFAULT_DARK_SURFACE = 0.15;

/** THE DARK HALF OF A MINTED PAIR — one rule: PRESERVE CONTRAST AGAINST THE SURFACE.
 *
 *      dark.L = L_darkSurface + (L_lightSurface − light.L)
 *
 *  Hue and chroma are held; only lightness moves. That single line covers what a branch on chroma
 *  would need two rules for, because it only ever asks ONE question — how far from the surface was
 *  this? — and that is the thing a reader actually perceives:
 *
 *      near-black text  L .15 -> ~.98   near-white   (a true inversion)
 *      white surface    L 1.0 -> ~.13   near-black   (a true inversion)
 *      indigo           L .51 -> ~.62   lighter indigo, hue held
 *
 *  A naive "invert the lightness" gets the first two right and the third wrong, turning a brand
 *  indigo into a pale wash. A naive "always lighten" gets the third right and inverts nothing.
 *
 *  The anchors come from the THEME when it names them, so a product with a deliberately dim light
 *  surface or a near-black dark surface derives against its own design rather than against ours. */
export function deriveDarkPair(
  light: string,
  surfaces?: { light: string; dark: string } | undefined
): string {
  const lch = toOklch(light);
  const lightSurface = surfaces ? toOklch(surfaces.light).l : DEFAULT_LIGHT_SURFACE;
  const darkSurface = surfaces ? toOklch(surfaces.dark).l : DEFAULT_DARK_SURFACE;
  // Clamped away from both extremes: pure black and pure white are almost never what a derived
  // colour should be, and clamping here keeps the gamut search below well-conditioned.
  const l = clamp(darkSurface + (lightSurface - lch.l), 0.05, 0.98);
  return toHex({ l, c: lch.c, h: lch.h });
}

/** The surface anchors a theme provides, if it names the conventional tokens. Returns undefined when
 *  neither exists, so `deriveDarkPair` falls back to its own defaults rather than to a half-theme. */
export function themeSurfaces(theme: Theme): { light: string; dark: string } | undefined {
  const surface = theme.colors['color.surface'] ?? theme.colors['color.bg'];
  return surface ? { light: surface.light, dark: surface.dark } : undefined;
}

// ---------------------------------------------------------------------------- distance & nearest

/** Perceptual distance in OKLab. Euclidean in Lab IS the perceptual metric — that is what the space
 *  is for — so no weighting is needed and none should be added. */
export function colorDistance(a: string, b: string): number {
  const x = toOklch(a);
  const y = toOklch(b);
  const ax = x.c * Math.cos((x.h * Math.PI) / 180);
  const ay = x.c * Math.sin((x.h * Math.PI) / 180);
  const bx = y.c * Math.cos((y.h * Math.PI) / 180);
  const by = y.c * Math.sin((y.h * Math.PI) / 180);
  return Math.sqrt((x.l - y.l) ** 2 + (ax - bx) ** 2 + (ay - by) ** 2);
}

/** Roughly "a human would call these the same colour". Tuned to catch a re-picked brand colour that
 *  is a shade off, without swallowing two deliberately distinct greys. */
export const NEAR_THRESHOLD = 0.04;

/** THE ANTI-SPRAWL MECHANISM. Minting on demand is what makes nine nearly-identical greys possible,
 *  so the picker offers the closest existing token before it makes a new one. Compares the LIGHT
 *  half only: that is the value the author just chose and is looking at. */
export function nearestColorToken(
  theme: Theme,
  hex: string
): { token: string; distance: number } | null {
  let best: { token: string; distance: number } | null = null;
  for (const [token, pair] of Object.entries(theme.colors)) {
    const distance = colorDistance(hex, pair.light);
    if (!best || distance < best.distance) best = { token, distance };
  }
  return best;
}

// ---------------------------------------------------------------------------- naming

/** Hue bands, in OKLCH degrees. These are NOT the HSL angles — OKLCH puts pure red near 29° and pure
 *  blue near 264° — so the numbers below were read off the actual conversion, not copied from a
 *  colour wheel. Each entry is the UPPER bound of its band. */
const HUES: readonly (readonly [number, string])[] = [
  [20, 'rose'],
  [45, 'red'],
  [70, 'orange'],
  [95, 'amber'],
  [120, 'yellow'],
  [140, 'lime'],
  [160, 'green'],
  [180, 'emerald'],
  [200, 'teal'],
  [225, 'cyan'],
  [255, 'blue'],
  [285, 'indigo'],
  [310, 'violet'],
  [335, 'purple'],
  [350, 'fuchsia'],
  [360, 'rose'],
];

/** A readable slug for a minted token — `#4F46E5` -> `indigo`.
 *
 *  Readability is the whole point: this string appears in the token list, in the Theme studio, and in
 *  the inspector's resolved readout. `custom_3` there would be no more informative than the hex it
 *  replaced, and a token nobody can read is a token nobody reuses. */
export function colorSlug(hex: string): string {
  const { l, c, h } = toOklch(hex);
  // Neutrals have no meaningful hue, and naming one after floating-point noise would give two greys
  // different family names.
  if (c < 0.03) {
    if (l > 0.9) return 'white';
    if (l < 0.15) return 'black';
    return 'gray';
  }
  return HUES.find(([upper]) => h < upper)?.[1] ?? 'rose';
}

// ---------------------------------------------------------------------------- internals

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const s = hex.trim().replace(/^#/, '');
  const full = s.length === 3 ? s.split('').map((ch) => ch + ch).join('') : s;
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16) / 255,
    g: parseInt(full.slice(2, 4), 16) / 255,
    b: parseInt(full.slice(4, 6), 16) / 255,
  };
}

function lchToRgb(l: number, c: number, h: number): { r: number; g: number; b: number } {
  const rad = (h * Math.PI) / 180;
  const lin = oklabToLinear({ l, a: c * Math.cos(rad), b: c * Math.sin(rad) });
  return { r: fromLinear(lin.r), g: fromLinear(lin.g), b: fromLinear(lin.b) };
}

/** A small tolerance, because the round trip is floating point and an exact `0 <= x <= 1` test would
 *  reject values that quantise to a perfectly valid byte. */
function inGamut(l: number, c: number, h: number): boolean {
  const { r, g, b } = lchToRgb(l, c, h);
  return [r, g, b].every((v) => v >= -0.0001 && v <= 1.0001);
}

const channel = (v: number): string =>
  Math.round(clamp(v, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0');
